'use client';
import styles from './side-tab.module.css';
import ActionButton from '@/components/action-button/action-button';
import { ToolTip } from '@/components/tooltip/tooltip';
import {
	faCircleInfo,
	faEnvelope,
} from '@awesome.me/kit-012d1b9af0/icons/classic/regular';
import Apply from '@/components/trial/trial-detail/side-tab/apply/apply';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faLinkedin,
	faSquareFacebook,
	faSquareWhatsapp,
	faSquareXTwitter,
	IconName,
	IconPrefix,
} from '@fortawesome/free-brands-svg-icons';
import ShareSocial from '@/components/share-social/share-social';
import { TrialDetails } from '@scitrials-sst/core/Next/types';
import { merge, getStudyStatus } from '@scitrials-sst/core/Next/lib';
import { formatDate } from '@scitrials-sst/core/Next/utils';

export default function SideTab({
	setTab,
	trial,
}: {
	setTab: React.Dispatch<
		React.SetStateAction<'overview' | 'details' | 'locations'>
	>;
	trial: Partial<TrialDetails>;
}) {
	const tags = merge(
		trial?.curated?.treatments ?? [],
		trial?.curated?.outcomes ?? []
	);
	const status = getStudyStatus(trial?.overallStatus ?? '');

	return (
		<div className={styles['side-tab']}>
			<div className={styles['side-action']}>
				<ActionButton wrap nctid={trial?.objectID!} />
			</div>

			<div className={styles['side-status']}>
				<div>
					<p className={styles.status}>STATUS</p>
					<ToolTip
						type='status'
						icon={faCircleInfo}
						title={status.title}
						content={status.content}
					/>
				</div>

				{status.title === 'Recruiting' && (
					<Apply prop={() => setTab('locations')} />
				)}

				<hr />
				{tags.map((tag, i) => (
					<ToolTip
						key={i}
						type='tag'
						icon={tag.icon as [IconPrefix, IconName]}
						title={tag.label}
						content={tag.description}
					/>
				))}
			</div>

			<div className={styles['side-share']}>
				<p>share the trial</p>
				<div className=''>
					<ShareSocial
						wrapper='facebook'
						url={`${process.env.NEXT_PUBLIC_BASE_URL}/trial/${trial?.objectID}`}
					>
						<FontAwesomeIcon
							icon={faSquareFacebook}
							size='2xl'
							color='#1877F2'
						/>
					</ShareSocial>
					<ShareSocial
						wrapper='twitter'
						url={`${process.env.NEXT_PUBLIC_BASE_URL}/trial/${trial?.objectID}`}
					>
						<FontAwesomeIcon
							icon={faSquareXTwitter}
							size='2xl'
							color='#000000'
						/>
					</ShareSocial>
					<ShareSocial
						wrapper='whatsapp'
						url={`${process.env.NEXT_PUBLIC_BASE_URL}/trial/${trial?.objectID}`}
					>
						<FontAwesomeIcon
							icon={faSquareWhatsapp}
							size='2xl'
							color='#25D366'
						/>
					</ShareSocial>
					<ShareSocial
						wrapper='linkedin'
						url={`${process.env.NEXT_PUBLIC_BASE_URL}/trial/${trial?.objectID}`}
					>
						<FontAwesomeIcon icon={faLinkedin} size='2xl' color='#2867B2' />
					</ShareSocial>
					<ShareSocial
						wrapper='email'
						url={`${process.env.NEXT_PUBLIC_BASE_URL}/trial/${trial?.objectID}`}
						subject={`SCI Trial - ${trial?.briefTitle}`}
						body={`Hi,

Have you seen this spinal cord injury trial on SciTrials.org?

Non-Invasive Interventions for Respiratory Recovery in Chronic Spinal Cord Injury
`}
					>
						<FontAwesomeIcon icon={faEnvelope} size='2xl' color='#777777' />
					</ShareSocial>
				</div>
			</div>

			<div className={styles['side-info']}>
				{trial?.studyType && (
					<>
						<p className={styles['info-title']}>Study Type</p>
						<p className={styles['info-text']}>{trial?.studyType}</p>
					</>
				)}

				{trial?.phases && (
					<>
						<p className={styles['info-title']}>Phase</p>
						<p className={styles['info-text']}>{trial?.phases?.join(', ')}</p>
					</>
				)}

				{trial?.investigatorFullName ? (
					<>
						<p className={styles['info-title']}>Investigator</p>
						<p className={styles['info-text']}>{trial?.investigatorFullName}</p>
					</>
				) : trial?.overallOfficialsName ? (
					<>
						<p className={styles['info-title']}>Investigator</p>
						<p className={styles['info-text']}>{trial?.overallOfficialsName}</p>
					</>
				) : null}

				{trial?.investigatorAffiliation ? (
					<>
						<p className={styles['info-title']}>Organization</p>
						<p className={styles['info-text']}>
							{trial?.investigatorAffiliation}
						</p>
					</>
				) : trial?.overallOfficialsAffiliation ? (
					<>
						<p className={styles['info-title']}>Organization</p>
						<p className={styles['info-text']}>
							{trial?.overallOfficialsAffiliation}
						</p>
					</>
				) : null}

				<hr
					style={{
						background: '#000',
						marginTop: '0.5rem',
					}}
				/>

				{trial?.startDateStructDate && (
					<>
						<p className={styles['info-title']}>Start Date</p>
						<p className={styles['info-text']}>
							{formatDate(trial?.startDateStructDate)}
						</p>
						{trial?.startDateStructType && (
							<p className={styles['info-text']}>
								({trial?.startDateStructType})
							</p>
						)}
					</>
				)}
				{trial?.completionDateStructDate && (
					<>
						<p className={styles['info-title']}>Completion Date</p>
						<p className={styles['info-text']}>
							{formatDate(trial?.completionDateStructDate)}
						</p>
						{trial?.completionDateStructType && (
							<p className={styles['info-text']}>
								({trial?.completionDateStructType})
							</p>
						)}
					</>
				)}
				<hr
					style={{
						background: '#000',
						marginTop: '0.5rem',
					}}
				/>

				{trial?.studyFirstPostDateStructDate && (
					<>
						<p className={styles['info-title']}>First Posted</p>
						<p className={styles['info-text']}>
							{formatDate(trial?.studyFirstPostDateStructDate)}
						</p>
					</>
				)}
				{trial?.lastUpdatePostDateStructDate && (
					<>
						<p className={styles['info-title']}>Last Update Posted</p>
						<p className={styles['info-text']}>
							{formatDate(trial?.lastUpdatePostDateStructDate)}
						</p>
					</>
				)}

				<a
					className={styles['info-title']}
					href={`https://clinicaltrials.gov/study/${trial?.objectID}`}
					target='_blank'
				>
					ClinicalTrials.gov Record <br />
					<span style={{ fontWeight: 600 }}>{trial?.objectID}</span>
				</a>
			</div>
		</div>
	);
}
